import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface CelebrationDay {
  id: string;
  title_sk: string;
  title_en: string | null;
  partner_id: string | null;
}

interface GroupBirthday {
  user_id: string;
  month: number;
  day: number;
  display_name: string;
}

interface DailyCelebrationResult {
  todayCelebrations: CelebrationDay[];
  todayMeniny: CelebrationDay[];
  todayBirthdays: GroupBirthday[];
  nearestBirthdays: (GroupBirthday & { dateLabel: string })[];
  isLoading: boolean;
}

function isLeapYear(year: number) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function daysUntilBirthday(month: number, day: number, today: Date): number {
  const year = today.getFullYear();
  let bdayDay = day;
  if (month === 2 && day === 29 && !isLeapYear(year)) {
    bdayDay = 28;
  }

  let next = new Date(year, month - 1, bdayDay);
  next.setHours(0, 0, 0, 0);

  if (next <= today) {
    const nextYear = year + 1;
    if (month === 2 && day === 29 && !isLeapYear(nextYear)) {
      bdayDay = 28;
    } else {
      bdayDay = day;
    }
    next = new Date(nextYear, month - 1, bdayDay);
    next.setHours(0, 0, 0, 0);
  }

  return Math.ceil((next.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

// Mock data for demo mode
const DEMO_CELEBRATIONS: CelebrationDay[] = [
  { id: 'demo-celeb', title_sk: 'Svetový deň úsmevu', title_en: 'World Smile Day', partner_id: null },
];
const DEMO_MENINY: CelebrationDay[] = [
  { id: 'demo-meniny', title_sk: 'Meniny: Demo', title_en: null, partner_id: null },
];
const DEMO_BIRTHDAYS: GroupBirthday[] = [
  { user_id: 'demo-bday', month: new Date().getMonth() + 1, day: new Date().getDate(), display_name: 'marek_k' },
];

export function useDailyCelebration(groupId: string | undefined, isDemo = false): DailyCelebrationResult {
  const now = new Date();
  const todayMonth = now.getMonth() + 1;
  const todayDay = now.getDate();

  // In demo mode, return mock data immediately
  if (isDemo) {
    return {
      todayCelebrations: DEMO_CELEBRATIONS,
      todayMeniny: DEMO_MENINY,
      todayBirthdays: DEMO_BIRTHDAYS,
      nearestBirthdays: [],
      isLoading: false,
    };
  }

  const { data: allTodayRows = [], isLoading: celebLoading } = useQuery({
    queryKey: ['celebration-days', todayMonth, todayDay],
    queryFn: async () => {
      const { data } = await supabase
        .from('celebration_days')
        .select('id, title_sk, title_en, partner_id')
        .eq('month', todayMonth)
        .eq('day', todayDay)
        .eq('is_active', true);
      return (data || []) as CelebrationDay[];
    },
    staleTime: 1000 * 60 * 60 * 6,
    refetchOnWindowFocus: true,
  });

  // Separate meniny (title_sk starts with "Meniny:") from other celebrations
  const todayCelebrations = allTodayRows.filter(c => !c.title_sk.startsWith('Meniny:'));
  const todayMeniny = allTodayRows.filter(c => c.title_sk.startsWith('Meniny:'));

  const { data: birthdays = [], isLoading: bdayLoading } = useQuery({
    queryKey: ['group-birthdays', groupId],
    queryFn: async () => {
      if (!groupId) return [];
      const { data } = await supabase
        .from('group_birthdays')
        .select('user_id, month, day')
        .eq('group_id', groupId);

      if (!data || data.length === 0) return [];

      const { data: profiles } = await supabase
        .rpc('get_group_member_profiles', { target_group_id: groupId });

      const nameMap: Record<string, string> = {};
      (profiles || []).forEach((p: any) => {
        nameMap[p.id] = p.user_id || 'user';
      });

      return data.map((b: any) => ({
        user_id: b.user_id,
        month: b.month,
        day: b.day,
        display_name: nameMap[b.user_id] || 'user',
      })) as GroupBirthday[];
    },
    enabled: !!groupId,
    staleTime: 1000 * 60 * 60 * 6,
    refetchOnWindowFocus: true,
  });

  // Today's birthdays
  const todayBirthdays = birthdays.filter((b) => {
    if (b.month === todayMonth && b.day === todayDay) return true;
    if (b.month === 2 && b.day === 29 && todayMonth === 2 && todayDay === 28 && !isLeapYear(now.getFullYear())) return true;
    return false;
  });

  // Nearest upcoming birthdays (only if no one has birthday today)
  let nearestBirthdays: (GroupBirthday & { dateLabel: string })[] = [];
  if (todayBirthdays.length === 0 && birthdays.length > 0) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let closestDays = Infinity;
    let closestList: GroupBirthday[] = [];

    for (const b of birthdays) {
      // Skip anyone whose birthday is today
      if (b.month === todayMonth && b.day === todayDay) continue;
      if (b.month === 2 && b.day === 29 && todayMonth === 2 && todayDay === 28 && !isLeapYear(now.getFullYear())) continue;

      const d = daysUntilBirthday(b.month, b.day, today);
      if (d > 0 && d < closestDays) {
        closestDays = d;
        closestList = [b];
      } else if (d > 0 && d === closestDays) {
        closestList.push(b);
      }
    }

    if (closestList.length > 0) {
      nearestBirthdays = closestList.map(b => ({
        ...b,
        dateLabel: `${b.day}.${b.month}`,
      }));
    }
  }

  return {
    todayCelebrations,
    todayMeniny,
    todayBirthdays,
    nearestBirthdays,
    isLoading: celebLoading || bdayLoading,
  };
}
